import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { User, Edit, Save, X, MapPin, Calendar, Heart, ArrowRight, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import Notification from '../components/Notification';
import ImageUpload from '../components/ImageUpload';
import ForgotPassword from '../components/ForgotPassword';
import config from '../config/config';

const MyAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login, register, updateProfile, refreshUserData, isLoggingIn } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Estado para modo admin
  const [isAdminEditing, setIsAdminEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserData, setEditingUserData] = useState(null);
  
  // Forzar scroll hacia arriba cuando se carga la página
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  useEffect(() => {
    if (location.state?.mode === "register") {
      setIsLogin(false);
    } else if (location.state?.mode === "forgot-password") {
      setShowForgotPassword(true);
    } else if (location.state?.mode === "adminEdit") {
      setIsAdminEditing(true);
      setEditingUserId(location.state.editingUserId);
      setIsEditing(true); // Activar modo edición automáticamente
      
      // Cargar datos del usuario a editar
      const loadEditingUser = async () => {
        try {
          const userData = await apiService.getUserById(location.state.editingUserId);
          
          // Transformar datos del backend al formato del frontend
          const transformedUser = {
            id: userData.id,
            username: userData.username,
            email: userData.email,
            name: userData.firstName || 'Usuario',
            age: userData.age,
            gender: userData.sex === 'male' ? 'Hombre' : userData.sex === 'female' ? 'Mujer' : 'Hombre',
            state: userData.location || 'Ciudad de México',
            municipio: userData.municipio || '',
            description: userData.bio || '',
            photoUrl: userData.profilePicture || userData.profile_picture || null,
            estadoCivil: userData.estadoCivil || '',
            sexoBusca: userData.sexoBusca || '',
            descripcionFisica: userData.descripcionFisica || '',
            educacion: userData.educacion || '',
            ocupacion: userData.ocupacion || '',
            tipoPersonaBusca: userData.tipoPersonaBusca || ''
          };
          
          setEditingUserData(transformedUser);
        } catch (error) {
          setNotification({
            isVisible: true,
            type: 'error',
            message: 'Error al cargar datos del usuario'
          });
        }
      };
      
      loadEditingUser();
    }
  }, [location.state]);

  // Refrescar datos del usuario cuando se carga el componente
  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        try {
          await refreshUserData();
        } catch (error) {}
      }
    };

    loadUserData();
  }, []); // Solo se ejecuta una vez al montar el componente
  const [formData, setFormData] = useState({
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    username: '',
    firstName: '',
    age: '',
    gender: '',
    soyGender: '', // Campo visual: lo que el usuario ve en "Soy"
    buscoGender: '', // Campo visual: lo que el usuario ve en "Busco"
    sexoBusca: '', // Campo interno: se calcula automáticamente para el backend
    location: '',
    municipio: '',
    bio: '',
    estadoCivil: '',
    descripcionFisica: '',
    educacion: '',
    ocupacion: '',
    tipoPersonaBusca: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({
    emailMatch: false,
    passwordMatch: false
  });
  const [notification, setNotification] = useState({
    isVisible: false,
    type: 'success',
    message: ''
  });
  // Admin: cambio de contraseña
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');
  const [adminPasswordLoading, setAdminPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showAdminPasswordForm, setShowAdminPasswordForm] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    name: user?.name || '',
    age: user?.age || '',
    gender: user?.gender || '',
    state: user?.state || '',
    municipio: user?.municipio || '',
    description: user?.description || '',
    estadoCivil: user?.estadoCivil || '',
    sexoBusca: user?.sexoBusca || '',
    buscoGender: user?.sexoBusca ? config.extractBuscoFromSexoBusca(user.sexoBusca) : '', // Extraer "busco" del sexoBusca completo
    educacion: user?.educacion || '',
    ocupacion: user?.ocupacion || '',
    descripcionFisica: user?.descripcionFisica || '',
    tipoPersonaBusca: user?.tipoPersonaBusca || ''
  });

  // Sincronizar los datos del formulario con los datos del usuario (o usuario siendo editado)
  useEffect(() => {
    const userData = isAdminEditing ? editingUserData : user;
    if (userData) {
      setProfileFormData({
        name: userData.name || '',
        age: userData.age || '',
        gender: userData.gender || '',
        state: userData.state || '',
        municipio: userData.municipio || '',
        description: userData.description || '',
        estadoCivil: userData.estadoCivil || '',
        sexoBusca: userData.sexoBusca || '',
        buscoGender: userData.sexoBusca ? config.extractBuscoFromSexoBusca(userData.sexoBusca) : '', // Extraer "busco"
        educacion: userData.educacion || '',
        ocupacion: userData.ocupacion || '',
        descripcionFisica: userData.descripcionFisica || '',
        tipoPersonaBusca: userData.tipoPersonaBusca || ''
      });
    }
  }, [user, editingUserData, isAdminEditing]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Si cambió "soyGender" o "buscoGender", recalcular sexoBusca
      if (name === 'soyGender' || name === 'buscoGender') {
        const soy = name === 'soyGender' ? value : prev.soyGender;
        const busco = name === 'buscoGender' ? value : prev.buscoGender;
        
        // Solo construir sexoBusca si ambos están seleccionados
        if (soy && busco) {
          newData.sexoBusca = config.buildSexoBusca(soy, busco);
        }
      }
      
      // Si cambió "gender", sincronizar con "soyGender"
      if (name === 'gender') {
        newData.soyGender = value;
        // Recalcular sexoBusca si buscoGender ya está seleccionado
        if (value && prev.buscoGender) {
          newData.sexoBusca = config.buildSexoBusca(value, prev.buscoGender);
        }
      }
      
      return newData;
    });
  };

  // Validación en tiempo real cuando cambian los campos de confirmación
  useEffect(() => {
    setValidationErrors({
      emailMatch: formData.email !== '' && formData.confirmEmail !== '' && formData.email !== formData.confirmEmail,
      passwordMatch: formData.password !== '' && formData.confirmPassword !== '' && formData.password !== formData.confirmPassword
    });
  }, [formData.email, formData.confirmEmail, formData.password, formData.confirmPassword]);

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    
    setProfileFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Si cambió "buscoGender", recalcular sexoBusca
      if (name === 'buscoGender') {
        const soy = prev.gender; // El género del usuario (soy)
        const busco = value; // El género que busca
        
        // Solo construir sexoBusca si ambos están disponibles
        if (soy && busco) {
          newData.sexoBusca = config.buildSexoBusca(soy, busco);
        }
      }
      
      // Si cambió "gender", recalcular sexoBusca
      if (name === 'gender') {
        const soy = value;
        const busco = prev.buscoGender;
        
        if (soy && busco) {
          newData.sexoBusca = config.buildSexoBusca(soy, busco);
        }
      }
      
      return newData;
    });
  };

  const showNotification = (type, message, duration = 5000) => {
    setNotification({
      isVisible: true,
      type,
      message
    });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
    setErrors({});
  };

  const handleForgotPasswordBack = () => {
    setShowForgotPassword(false);
    setErrors({});
  };

  const handleForgotPasswordSuccess = (message) => {
    // El componente ForgotPassword ya maneja su propia UI de éxito
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validar que los campos de confirmación coincidan (solo para registro)
    if (!isLogin) {
      if (formData.email !== formData.confirmEmail) {
        setError('Los correos electrónicos no coinciden');
        setLoading(false);
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Las contraseñas no coinciden');
        setLoading(false);
        return;
      }
    }

    try {
      if (isLogin) {
        await login(formData.email, formData.password, () => {
          // Redireccionar a la página principal después del login exitoso
          setTimeout(() => {
            navigate('/');
          }, 1000); // Pequeño delay para que el usuario vea la notificación
        });
        showNotification('success', '¡Inicio de sesión exitoso!');
      } else {
        const result = await register(formData, () => {
          // Redireccionar a la página principal después del registro exitoso
          setTimeout(() => {
            navigate('/');
          }, 1000); // Pequeño delay para que el usuario vea la notificación
        });
        
        // Mostrar mensaje del backend si está disponible
        const message = result.registrationMessage || '¡Usuario registrado exitosamente!';
        showNotification('success', message);
        
        // Limpiar formulario
        setFormData({
          email: '',
          confirmEmail: '',
          password: '',
          confirmPassword: '',
          username: '',
          firstName: '',
          age: '',
          gender: '',
          sexoBusca: '',
          location: '',
          bio: '',
          estadoCivil: '',
          descripcionFisica: '',
          educacion: '',
          ocupacion: '',
          tipoPersonaBusca: ''
        });
        
        // Cambiar a modo login después de un delay
        setTimeout(() => {
          setIsLogin(true);
          showNotification('info', 'Ahora puedes iniciar sesión con tu nueva cuenta');
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
      showNotification('error', err.message);
      
      // Si es error 409 (email ya existe), sugerir iniciar sesión
      if (err.message.includes('ya está registrado') && !isLogin) {
        setError(
          <div>
            <p className="mb-2">{err.message}</p>
            <button
              onClick={() => {
                setIsLogin(true);
                setError('');
                setFormData(prev => ({ 
                  ...prev, 
                  password: '', 
                  confirmEmail: '',
                  confirmPassword: ''
                }));
              }}
              className="text-primary hover:underline font-medium"
            >
              ¿Ya tienes cuenta? Haz clic aquí para iniciar sesión
            </button>
          </div>
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    showNotification('info', 'Guardando cambios...');
    
    try {
      if (isAdminEditing) {
        // Admin editando otro usuario
        // Usar el mismo formato que cuando el usuario se edita a sí mismo
        const userData = {
          name: profileFormData.name,
          age: parseInt(profileFormData.age),
          gender: profileFormData.gender,
          state: profileFormData.state,
          municipio: profileFormData.municipio,
          description: profileFormData.description,
          estadoCivil: profileFormData.estadoCivil,
          sexoBusca: profileFormData.sexoBusca,
          educacion: profileFormData.educacion,
          ocupacion: profileFormData.ocupacion,
          descripcionFisica: profileFormData.descripcionFisica,
          tipoPersonaBusca: profileFormData.tipoPersonaBusca
        };
        
        await apiService.updateUser(editingUserId, userData);
        
        setTimeout(() => {
          showNotification('success', '¡Usuario actualizado exitosamente!');
          setIsEditing(false);
          // Regresar al perfil del usuario
          navigate(`/perfil/${editingUserId}`);
        }, 1000);
      } else {
        // Usuario editando su propio perfil
        await updateProfile(profileFormData);
        
        // Delay para que el usuario vea el feedback
        setTimeout(() => {
          showNotification('success', '¡Perfil actualizado exitosamente!');
          setIsEditing(false);
        }, 1000);
      }
    } catch (error) {
      showNotification('error', 'Error al actualizar el perfil');
    } finally {
      setTimeout(() => {
        setIsSaving(false);
      }, 1500);
    }
  };

  const handleCancel = () => {
    if (isAdminEditing) {
      // Si el admin está editando, regresar al perfil del usuario
      navigate(`/perfil/${editingUserId}`);
      return;
    }
    
    // Animación suave al cancelar
    showNotification('info', 'Cambios cancelados');
    
    setTimeout(() => {
      // Restaurar los datos originales del usuario
      if (user) {
        setProfileFormData({
          name: user.name || '',
          age: user.age || '',
          gender: user.gender || '',
          state: user.state || '',
          municipio: user.municipio || '',
          description: user.description || '',
          estadoCivil: user.estadoCivil || '',
          sexoBusca: user.sexoBusca || '',
          educacion: user.educacion || '',
          ocupacion: user.ocupacion || '',
          descripcionFisica: user.descripcionFisica || '',
          tipoPersonaBusca: user.tipoPersonaBusca || ''
        });
      }
      setIsEditing(false);
    }, 500);
  };

  const validateAdminPassword = (pwd) => {
    // Solo mínimo 6 caracteres
    return typeof pwd === 'string' && pwd.length >= 6;
  };

  const handleAdminPasswordSave = async () => {
    setAdminPasswordError('');
    if (!adminPassword || !adminPasswordConfirm) {
      setAdminPasswordError('Completa ambos campos');
      return;
    }
    if (adminPassword !== adminPasswordConfirm) {
      setAdminPasswordError('Las contraseñas no coinciden');
      return;
    }
    if (!validateAdminPassword(adminPassword)) {
      setAdminPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      setAdminPasswordLoading(true);
      await apiService.adminUpdateUserPassword(editingUserId, adminPassword);
      setAdminPassword('');
      setAdminPasswordConfirm('');
      setShowAdminPasswordForm(false); // Cerrar el formulario después de guardar
      showNotification('success', 'Contraseña actualizada');
    } catch (e) {
      showNotification('error', e.message || 'Error al actualizar contraseña');
    } finally {
      setAdminPasswordLoading(false);
    }
  };

  const handleUserPasswordSave = async () => {
    setPasswordChangeError('');
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordChangeError('Completa todos los campos');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordChangeError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordChangeError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      setPasswordChangeLoading(true);
      await apiService.changeUserPassword(isAdminEditing ? editingUserId : user.id, currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowPasswordForm(false); // Cerrar el formulario después de guardar
      showNotification('success', 'Contraseña actualizada');
    } catch (e) {
      setPasswordChangeError(e.message || 'Error al actualizar contraseña');
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handleImageUpdate = (newImageUrl) => {
    try {
      // Actualizar la imagen usando el contexto de autenticación
      const updatedData = { photoUrl: newImageUrl, hasPhoto: true };
      updateProfile(updatedData);
      showNotification('success', '¡Foto de perfil actualizada exitosamente!');
    } catch (error) {
      showNotification('error', 'Error al actualizar la imagen de perfil');
    }
  };

  const handleImageDelete = () => {
    try {
      // Eliminar la imagen usando el contexto de autenticación
      const updatedData = { photoUrl: null, hasPhoto: false };
      updateProfile(updatedData);
      showNotification('success', 'Foto de perfil eliminada exitosamente');
    } catch (error) {
      showNotification('error', 'Error al eliminar la imagen de perfil');
    }
  };

  // Si el usuario no está autenticado, mostrar formulario de login/registro
  if (!user) {
    return (
      <div className="min-h-screen bg-background py-8">
        <Notification
          type={notification.type}
          message={notification.message}
          isVisible={notification.isVisible}
          onClose={hideNotification}
          autoClose={true}
          duration={5000}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card">
            {/* Header */}
            <div className="text-center mb-8">
              <User className="h-16 w-16 text-primary mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-text mb-2">Mi Cuenta</h1>
              <p className="text-gray-600">Inicia sesión o regístrate para acceder a tu perfil</p>
            </div>

            {/* Formulario de Login/Registro */}
            <div className="max-w-md mx-auto">
              <div className="flex space-x-4 mb-6">
                <button
                  onClick={() => setIsLogin(true)}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${
                    isLogin
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Iniciar Sesión
                </button>
                <button
                  onClick={() => setIsLogin(false)}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${
                    !isLogin
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Registrarse
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <>
                    {/* Campos Obligatorios */}
                    <div className="form-section border-b pb-4 mb-4">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Información Básica (Obligatoria)</h3>
                      
                      {/* Soy y Busco juntos */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="form-label">Soy *</label>
                          <select
                            name="Gender"
                            value={formData.Gender}
                            onChange={handleInputChange}
                            className="input-field"
                            required
                          >
                            <option value="">Seleccionar</option>
                            <option value="Hombre">Hombre</option>
                            <option value="Mujer">Mujer</option>
                            <option value="No Binario">No Binario</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Busco *</label>
                          <select
                            name="buscoGender"
                            value={formData.buscoGender}
                            onChange={handleInputChange}
                            className="input-field"
                            required
                          >
                            <option value="">Seleccionar</option>
                            {config.GENDER_OPTIONS.map((option, index) => (
                              <option key={`busco-${index}`} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Edad */}
                      <div className="mt-4">
                        <label className="form-label">Edad * (mínimo 18 años)</label>
                        <input
                          type="number"
                          name="age"
                          value={formData.age}
                          onChange={handleInputChange}
                          className="input-field"
                          min="18"
                          max="100"
                          required
                        />
                      </div>

                      {/* Usuario */}
                      {!isLogin && (
                        <div>
                          <label className="form-label">Nombre de usuario *</label>
                          <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            className="input-field"
                            required
                            placeholder="usuario123"
                          />
                        </div>
                      )}
                    </div>

                    {/* Campos Opcionales */}
                    <div className="form-section">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Información Adicional</h3>
                      
                      <div className="space-y-4">
                        {/* Estado */}
                        <div>
                          <label className="form-label">Estado</label>
                          <select
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            className="input-field"
                            required
                          >
                            <option value="">Seleccionar estado</option>
                            {config.MEXICAN_STATES.map((state, index) => (
                              <option key={`${state}-${index}`} value={state}>{state}</option>
                            ))}
                          </select>
                        </div>

                        {/* Municipio */}
                        <div>
                          <label className="form-label">Municipio</label>
                          <input
                            type="text"
                            name="municipio"
                            value={formData.municipio}
                            onChange={handleInputChange}
                            placeholder="Ej: Álvaro Obregón, Coyoacán, etc."
                            maxLength="255"
                            className="input-field"
                            required
                          />
                        </div>

                        {/* Estado Civil y Educación juntos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="form-label">Estado Civil</label>
                            <select
                              name="estadoCivil"
                              value={formData.estadoCivil}
                              onChange={handleInputChange}
                              className="input-field"
                              required
                            >
                              <option value="">Seleccionar</option>
                              <option value="Soltero(a)">Soltero(a)</option>
                              <option value="Divorciado(a)">Divorciado(a)</option>
                              <option value="Viudo(a)">Viudo(a)</option>
                              <option value="Separado(a)">Separado(a)</option>
                              <option value="Relacion Abierta">Relacion Abierta</option>
                              <option value="Prefiero no decir">Prefiero no decir</option>
                            </select>
                          </div>
                          <div>
                            <label className="form-label">Educación</label>
                            <select
                              name="educacion"
                              value={formData.educacion}
                              onChange={handleInputChange}
                              className="input-field"
                              required
                            >
                              <option value="">Seleccionar</option>
                              <option value="Educación Básica">Educación Básica</option>
                              <option value="Preparatoria">Preparatoria</option>
                              <option value="Técnico">Técnico</option>
                              <option value="Licenciatura">Licenciatura</option>
                              <option value="Maestría">Maestría</option>
                              <option value="Doctorado">Doctorado</option>
                            </select>
                          </div>
                        </div>

                        {/* Ocupación */}
                        <div>
                          <label className="form-label">Ocupación</label>
                          <select
                            name="ocupacion"
                            value={formData.ocupacion}
                            onChange={handleInputChange}
                            className="input-field"
                            required
                          >
                            <option value="">Seleccionar</option>
                            <option value="Estudiante">Estudiante</option>
                            <option value="Empleado">Empleado</option>
                            <option value="Negociante">Negociante</option>
                            <option value="Tecnico">Técnico</option>
                            <option value="Administrativo">Administrativo</option>
                            <option value="Profesionista">Profesionista</option>
                            <option value="Ejecutivo">Ejecutivo</option>
                            <option value="Empresario">Empresario</option>
                            <option value="Prefiero no decir">Prefiero no decir</option>
                          </select>
                        </div>

                        {/* Biografía */}
                        <div>
                          <label className="form-label">Biografía</label>
                          <textarea
                            name="bio"
                            value={formData.bio}
                            onChange={handleInputChange}
                            className="input-field"
                            rows="3"
                            placeholder="Cuéntanos sobre ti..."
                            required
                          />
                        </div>

                        {/* Descripción Física */}
                        <div>
                          <label className="form-label">Descripción Física</label>
                          <input
                            type="text"
                            name="descripcionFisica"
                            value={formData.descripcionFisica}
                            onChange={handleInputChange}
                            className="input-field"
                            placeholder="Ej: Mido 1.75m, ojos cafés..."
                            required
                          />
                        </div>

                        {/* Tipo de Persona que Busco */}
                        <div>
                          <label className="form-label">Tipo de Persona que Busco</label>
                          <textarea
                            name="tipoPersonaBusca"
                            value={formData.tipoPersonaBusca}
                            onChange={handleInputChange}
                            className="input-field"
                            rows="2"
                            placeholder="Ej: Busco una persona sincera, divertida..."
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                 <div>
                  <label className="form-label">Correo electrónico o nombre de usuario *</label>
                  <input
                    type="text"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`input-field ${validationErrors.emailMatch ? 'border-red-500' : ''}`}
                    required
                    placeholder="tu@email.com o tu_usuario"
                  />
                </div>

                 {/* Campo de confirmación de email solo para registro */}
                 {!isLogin && (
                   <div>
                     <label className="form-label">Confirmar correo electrónico *</label>
                     <input
                       type="email"
                       name="confirmEmail"
                       value={formData.confirmEmail}
                       onChange={handleInputChange}
                       className={`input-field ${validationErrors.emailMatch ? 'border-red-500' : ''}`}
                       required
                       placeholder="tu@email.com"
                     />
                     {validationErrors.emailMatch && (
                       <p className="text-red-500 text-sm mt-1">Los correos electrónicos no coinciden</p>
                     )}
                   </div>
                 )}

                 {/* Usuario */}
                
                 <div>
                   <label className="form-label">Contraseña *</label>
                   <input
                     type="password"
                     name="password"
                     value={formData.password}
                     onChange={handleInputChange}
                     className={`input-field ${validationErrors.passwordMatch ? 'border-red-500' : ''}`}
                     required
                     placeholder="Mínimo 6 caracteres"
                   />
                 </div>

                 {/* Campo de confirmación de contraseña solo para registro */}
                 {!isLogin && (
                   <div>
                     <label className="form-label">Confirmar contraseña *</label>
                     <input
                       type="password"
                       name="confirmPassword"
                       value={formData.confirmPassword}
                       onChange={handleInputChange}
                       className={`input-field ${validationErrors.passwordMatch ? 'border-red-500' : ''}`}
                       required
                       placeholder="Mínimo 6 caracteres"
                     />
                     {validationErrors.passwordMatch && (
                       <p className="text-red-500 text-sm mt-1">Las contraseñas no coinciden</p>
                     )}
                   </div>
                 )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                 <button
                   type="submit"
                   disabled={loading || isLoggingIn}
                   className={`btn-primary w-full flex items-center justify-center space-x-2 transition-all duration-200 ${
                     loading || isLoggingIn ? 'opacity-75 cursor-not-allowed' : 'hover:scale-105 active:scale-95'
                   }`}
                 >
                   {loading || isLoggingIn ? (
                     <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                   ) : (
                     <>
                       <span>{isLogin ? 'Iniciar Sesión' : 'Registrarse'}</span>
                       <ArrowRight className="h-4 w-4" />
                     </>
                   )}
                 </button>

               
               </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si el usuario está autenticado, mostrar su perfil
  // Determinar qué datos mostrar (usuario actual o usuario siendo editado)
  const displayUser = isAdminEditing ? editingUserData : user;
  
  // Si el admin está editando pero aún no se cargaron los datos, mostrar loading
  if (isAdminEditing && !editingUserData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando datos del usuario...</p>
        </div>
      </div>
    );
  }
  
  // Si se está mostrando el formulario de recuperación de contraseña
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-background py-8">
        <ForgotPassword 
          onBack={handleForgotPasswordBack}
          onSuccess={handleForgotPasswordSuccess}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="card">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <User className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold text-text">
                {isAdminEditing ? `Editando Usuario: ${editingUserData?.username || ''}` : 'Mi Cuenta'}
              </h1>
            </div>
            {!isAdminEditing && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  isEditing
                    ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    : 'bg-primary text-white hover:bg-primary-hover'
                }`}
              >
                {isEditing ? (
                  <>
                    <X className="h-4 w-4" />
                    <span>Cancelar</span>
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4" />
                    <span>Editar Perfil</span>
                  </>
                )}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Foto de perfil */}
            <div className="lg:col-span-1">
              <div className="text-center">
                <ImageUpload
                  userId={displayUser.id}
                  currentImageUrl={displayUser.photoUrl}
                  onImageUpdate={handleImageUpdate}
                  onImageDelete={handleImageDelete}
                  userGender={displayUser.gender}
                />
                
                <div className="mt-4">
                  <h2 className="text-2xl font-bold text-text">{displayUser.username}</h2>
                  <p className="text-gray-600">@{displayUser.username}</p>
                  {isAdminEditing && displayUser.email && (
                    <p className="text-gray-600 mt-1">{displayUser.email}</p>
                  )}
                  {!isAdminEditing && (
                    <div className="flex items-center justify-center space-x-2 mt-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-green-600">En línea</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Información del perfil */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {/* Información básica */}
                <div>
                  <h3 className="text-lg font-semibold text-text mb-4">Información Personal</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Nombre de usuario</label>
                      <p className="text-gray-900">{displayUser.username}</p>
                    </div>
                    
                    <div>
                      <label className="form-label">Edad</label>
                      {isEditing ? (
                        <input
                          type="number"
                          name="age"
                          value={profileFormData.age}
                          onChange={handleProfileInputChange}
                          className="input-field"
                          min="18"
                          max="100"
                        />
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-900">{displayUser.age} años</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="form-label">Género</label>
                      {isEditing ? (
                        <select
                          name="gender"
                          value={profileFormData.gender}
                          onChange={handleProfileInputChange}
                          className="input-field"
                        >
                          <option value="">Seleccionar</option>
                          <option value="Hombre">Hombre</option>
                          <option value="Mujer">Mujer</option>
                        </select>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Heart className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-900">{displayUser.gender}</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="form-label">Estado</label>
                      {isEditing ? (
                        <select
                          name="state"
                          value={profileFormData.state}
                          onChange={handleProfileInputChange}
                          className="input-field"
                        >
                          <option value="">Seleccionar estado</option>
                          {config.MEXICAN_STATES.map((state, index) => (
                            <option key={`${state}-${index}`} value={state}>{state}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">{displayUser.state}</span>
                          </div>
                          {displayUser.municipio && (
                            <div className="flex items-center space-x-2 ml-6">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              <span className="text-gray-700 text-sm">{displayUser.municipio}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Municipio */}
                    <div>
                      <label className="form-label">Municipio</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="municipio"
                          value={profileFormData.municipio}
                          onChange={handleProfileInputChange}
                          placeholder="Ej: Álvaro Obregón, Coyoacán, etc."
                          maxLength="255"
                          className="input-field"
                        />
                      ) : (
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-900">{displayUser.municipio || 'No especificado'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Información Adicional */}
                <div>
                  <h3 className="text-lg font-semibold text-text mb-4">Información Adicional</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Estado Civil</label>
                      {isEditing ? (
                        <select
                          name="estadoCivil"
                          value={profileFormData.estadoCivil}
                          onChange={handleProfileInputChange}
                          className="input-field"
                        >
                          <option value="">Seleccionar</option>
                          <option value="Soltero(a)">Soltero(a)</option>
                          <option value="Divorciado(a)">Divorciado(a)</option>
                          <option value="Viudo(a)">Viudo(a)</option>
                          <option value="Separado(a)">Separado(a)</option>
                          <option value="Relacion Abierta">Relacion Abierta</option>
                          <option value="Prefiero no decir">Prefiero no decir</option>
                        </select>
                      ) : (
                        <p className="text-gray-900">{displayUser.estadoCivil || "No especificado"}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="form-label">Educación</label>
                      {isEditing ? (
                        <select
                          name="educacion"
                          value={profileFormData.educacion}
                          onChange={handleProfileInputChange}
                          className="input-field"
                        >
                          <option value="">Seleccionar</option>
                          <option value="Educación Básica">Educación Básica</option>
                          <option value="Preparatoria">Preparatoria</option>
                          <option value="Técnico">Técnico</option>
                          <option value="Licenciatura">Licenciatura</option>
                          <option value="Maestría">Maestría</option>
                          <option value="Doctorado">Doctorado</option>
                        </select>
                      ) : (
                        <p className="text-gray-900">{displayUser.educacion || "No especificado"}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="form-label">Ocupación</label>
                      {isEditing ? (
                        <select
                          name="ocupacion"
                          value={profileFormData.ocupacion}
                          onChange={handleProfileInputChange}
                          className="input-field"
                        >
                          <option value="">Seleccionar</option>
                          <option value="Estudiante">Estudiante</option>
                          <option value="Empleado">Empleado</option>
                          <option value="Negociante">Negociante</option>
                          <option value="Tecnico">Técnico</option>
                          <option value="Administrativo">Administrativo</option>
                          <option value="Profesionista">Profesionista</option>
                          <option value="Ejecutivo">Ejecutivo</option>
                          <option value="Empresario">Empresario</option>
                          <option value="Prefiero no decir">Prefiero no decir</option>
                        </select>
                      ) : (
                        <p className="text-gray-900">{displayUser.ocupacion || "No especificado"}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="form-label">Descripción Física</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="descripcionFisica"
                          value={profileFormData.descripcionFisica}
                          onChange={handleProfileInputChange}
                          className="input-field"
                          placeholder="Ej: Mido 1.75m, ojos cafés..."
                        />
                      ) : (
                        <p className="text-gray-900">{displayUser.descripcionFisica || "No especificado"}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label className="form-label">Busco</label>
                    {isEditing ? (
                      <select
                        name="buscoGender"
                        value={profileFormData.buscoGender}
                        onChange={handleProfileInputChange}
                        className="input-field"
                      >
                        <option value="">Seleccionar</option>
                        {config.GENDER_OPTIONS.map((option, index) => (
                          <option key={`busco-edit-${index}`} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-gray-900">{displayUser.sexoBusca ? config.extractBuscoFromSexoBusca(displayUser.sexoBusca) : "No especificado"}</p>
                    )}
                  </div>
                  
                  <div className="mt-4">
                    <label className="form-label">Tipo de Persona que Busco</label>
                    {isEditing ? (
                      <textarea
                        name="tipoPersonaBusca"
                        value={profileFormData.tipoPersonaBusca}
                        onChange={handleProfileInputChange}
                        className="input-field"
                        rows="2"
                        placeholder="Ej: Busco una persona sincera, divertida..."
                      />
                    ) : (
                      <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
                        {displayUser.tipoPersonaBusca || "No especificado"}
                      </p>
                    )}
                  </div>
                </div>

                {/* Descripción */}
                <div>
                  <label className="form-label">Sobre mí</label>
                  {isEditing ? (
                    <textarea
                      name="description"
                      value={profileFormData.description}
                      onChange={handleProfileInputChange}
                      rows="4"
                      className="input-field"
                      placeholder="Cuéntanos sobre ti..."
                    />
                  ) : (
                    <p className="text-gray-900 bg-gray-50 p-4 rounded-lg">
                      {displayUser.description || "No has agregado una descripción aún."}
                    </p>
                  )}
                </div>

                {/* Información de contacto */}
                <div>
                  <h3 className="text-lg font-semibold text-text mb-4">Información de Contacto</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">Email:</span>
                      <span className="text-gray-900">{displayUser.email || 'No disponible'}</span>
                    </div>
                  </div>
                </div>

                {!isAdminEditing && (
                  <div className="mt-6 border rounded-lg bg-gray-50 overflow-hidden">
                    {/* Botón para desplegar/ocultar */}
                    <button
                      onClick={() => {
                        setShowPasswordForm(!showPasswordForm);
                        // Limpiar campos y errores al cerrar
                        if (showPasswordForm) {
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmNewPassword('');
                          setPasswordChangeError('');
                        }
                      }}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <Lock className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold text-text">Cambiar contraseña</h3>
                      </div>
                      {showPasswordForm ? (
                        <ChevronUp className="h-5 w-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-600" />
                      )}
                    </button>

                    {/* Formulario (solo visible cuando showPasswordForm es true) */}
                    {showPasswordForm && (
                      <div className="p-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="form-label">Contraseña actual</label>
                            <input
                              type="password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              className="input-field"
                              placeholder="Tu contraseña actual"
                            />
                          </div>
                          <div>
                            <label className="form-label">Nueva contraseña</label>
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="input-field"
                              placeholder="Mínimo 6 caracteres"
                            />
                          </div>
                          <div>
                            <label className="form-label">Confirmar nueva contraseña</label>
                            <input
                              type="password"
                              value={confirmNewPassword}
                              onChange={(e) => setConfirmNewPassword(e.target.value)}
                              className="input-field"
                              placeholder="Repite la contraseña"
                            />
                          </div>
                        </div>
                        {passwordChangeError && (
                          <p className="text-red-600 text-sm mt-2">{passwordChangeError}</p>
                        )}
                        <div className="mt-3">
                          <button
                            onClick={handleUserPasswordSave}
                            disabled={passwordChangeLoading}
                            className={`btn-outline ${passwordChangeLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            {passwordChangeLoading ? 'Actualizando…' : 'Actualizar contraseña'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Admin: Cambiar contraseña del usuario */}
                {isAdminEditing && (
                  <div className="mt-6 border rounded-lg bg-gray-50 overflow-hidden">
                    {/* Botón para desplegar/ocultar */}
                    <button
                      onClick={() => {
                        setShowAdminPasswordForm(!showAdminPasswordForm);
                        // Limpiar campos y errores al cerrar
                        if (showAdminPasswordForm) {
                          setAdminPassword('');
                          setAdminPasswordConfirm('');
                          setAdminPasswordError('');
                        }
                      }}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <Lock className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold text-text">Cambiar contraseña (Admin)</h3>
                      </div>
                      {showAdminPasswordForm ? (
                        <ChevronUp className="h-5 w-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-600" />
                      )}
                    </button>

                    {/* Formulario (solo visible cuando showAdminPasswordForm es true) */}
                    {showAdminPasswordForm && (
                      <div className="p-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="form-label">Nueva contraseña</label>
                            <input
                              type="password"
                              value={adminPassword}
                              onChange={(e) => setAdminPassword(e.target.value)}
                              className="input-field"
                              placeholder="Ej: NuevoPass123"
                            />
                          </div>
                          <div>
                            <label className="form-label">Confirmar contraseña</label>
                            <input
                              type="password"
                              value={adminPasswordConfirm}
                              onChange={(e) => setAdminPasswordConfirm(e.target.value)}
                              className="input-field"
                              placeholder="Repite la contraseña"
                            />
                          </div>
                        </div>
                        {adminPasswordError && (
                          <p className="text-red-600 text-sm mt-2">{adminPasswordError}</p>
                        )}
                        <div className="mt-3">
                          <button
                            onClick={handleAdminPasswordSave}
                            disabled={adminPasswordLoading}
                            className={`btn-outline ${adminPasswordLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            {adminPasswordLoading ? 'Actualizando…' : 'Actualizar contraseña'}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Mínimo 6 caracteres.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Botones de acción */}
                {isEditing && (
                  <div className="flex space-x-4 pt-4">
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className={`btn-primary flex items-center space-x-2 transition-all duration-200 ${
                        isSaving ? 'opacity-75 cursor-not-allowed' : 'hover:scale-105 active:scale-95'
                      }`}
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Guardando...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          <span>Guardar Cambios</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isSaving}
                      className={`btn-outline transition-all duration-200 ${
                        isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'
                      }`}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;

